// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './OwnerNoteSlides.module.css'
import { DepartureChips, ownershipLine } from './NoteAnswers'
import NotePager from './NotePager'
import { buyAgainMark } from 'lib/owner-notes/band-copy'
import { useNoteCarousel } from 'lib/owner-notes/note-carousel'
import type { OwnerNotePublic, OwnerQuestionSetDef } from 'lib/dealhunt/types'
import MemberName from 'components/community/MemberName'

interface OwnerNoteSlidesProps {
  notes: OwnerNotePublic[]
  questions: OwnerQuestionSetDef | null
  /** With a title the pager sits in a header row above the track ("In their words · 1 / 3 ‹ ›"). */
  title?: string
}

/**
 * Notes one at a time — the reader under the gallery (design 4b, 2026-09-22): the note's meta,
 * its sentence, its departure chips, its buy-again mark, and a "1 / 3 ‹ ›" pager. Native scroll-snap track (`useNoteCarousel`, #118);
 * one note is a bare slide with no pager. Every note is in the HTML — the pager only scrolls.
 */
const OwnerNoteSlides: React.VFC<OwnerNoteSlidesProps> = ({ notes, questions, title }) => {
  const { trackRef, active, goTo } = useNoteCarousel(notes.length)
  const many = notes.length > 1
  const pager = many ? (
    <NotePager
      active={active}
      count={notes.length}
      goTo={goTo}
      className={styles.pager}
      countClassName={styles.pagerCount}
      buttonClassName={styles.pagerBtn}
    />
  ) : null
  const slide = (note: OwnerNotePublic, i: number): JSX.Element => (
    <article
      key={note.id}
      className={styles.slide}
      data-note-id={note.id}
      aria-label={many ? `Owner review ${i + 1} of ${notes.length}` : undefined}
    >
      <p className={styles.meta}>
        {/* DealHunt #218: the shown name beside the handle when the member shows one. */}
        <span className={styles.handle}>
          {note.display_name ? (
            <MemberName name={note.display_name} handle={note.handle} />
          ) : (
            `@${note.handle}`
          )}
        </span>
        {' · '}
        <span
          className={clsx(styles.badge, {
            [styles.badgeVerified]: note.photo_status === 'verified',
          })}
        >
          {note.photo_label}
        </span>
      </p>
      <p className={styles.owned}>{ownershipLine(note.answers, questions, note.published_at)}</p>
      <p className={styles.text}>{note.note}</p>
      <DepartureChips answers={note.answers} questions={questions} />
      <div className={styles.foot}>
        {typeof note.answers.buy_again === 'boolean' && (
          <span className={clsx(styles.buy, note.answers.buy_again ? styles.buyYes : styles.buyNo)}>
            {buyAgainMark(note.answers.buy_again)}
          </span>
        )}
        {!title && pager}
      </div>
    </article>
  )
  const track = many ? (
    <div className={styles.track} ref={trackRef}>
      {notes.map(slide)}
    </div>
  ) : (
    <div className={styles.single}>{notes.map(slide)}</div>
  )
  if (!title) {
    return track
  }
  return (
    <div className={styles.titled}>
      <div className={styles.titleRow}>
        <span className={styles.title}>{title}</span>
        {pager}
      </div>
      {track}
    </div>
  )
}

export default OwnerNoteSlides
